import {
  Box, Divider, Grid, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Select, MenuItem,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Group } from '@visx/group';
import { Treemap } from '@visx/hierarchy';
import { withTooltip } from '@visx/tooltip';
import { hierarchy } from 'd3-hierarchy';
import meanBy from "lodash.meanby";
import sumBy from 'lodash.sumby';
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { dataConstants } from "../../../constants";
import { formatCount } from "../../../utils/D3/helper";
import { getURL } from "../../../utils/helpers";
import HeatMapTable from "./HeatMapTable";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const isExponent = num => {
  const n = num.toString();
  return /[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)/g.test(n);
};

export const roundNum = (num, round = 2) => {
  if (!num || isNaN(parseFloat(num))) return num;
  if (isExponent(num)) {
    const [base, exponent] = num.toString().split('e');
    return parseFloat(`${parseFloat(base).toFixed(round)}e${exponent}`);
  }
  return parseFloat((Math.round(parseFloat(num) * Math.pow(10, round)) / Math.pow(10, round)).toFixed(round));
};

export const getPercent = (a, b) => {
  return roundNum((parseFloat(a) - parseFloat(b)) * 100 / parseFloat(b), 2);
};

const Card = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  overflow: 'hidden',
}));

const PctPill = ({ value, sx = {} }) => {
  const num = parseFloat(value);
  if (isNaN(num)) return <Typography variant="caption" sx={{ color: '#999', ...sx }}>&mdash;</Typography>;
  const isPositive = num >= 0;
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'inline-block',
        px: 1,
        py: 0.25,
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '.78rem',
        backgroundColor: isPositive ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)',
        color: isPositive ? '#059669' : '#dc2626',
        ...sx,
      }}
    >
      {num.toFixed(2)}%
    </Typography>
  );
};

const { SECTOR_OPTIONS } = dataConstants;

const periodLabels = {
  weightedPCHG: '1 Day',
  weightedPCHG7: '7 Days',
  weightedPCHG30: '30 Days',
  weightedPCHG180: '6 Months',
  weightedPCHG365: '1 Year',
};

const calculateAverages = (data) => {
  const fields = ['weight', 'medianPCHG', 'medianPCHG7', 'medianPCHG30', 'medianPCHG180', 'medianPCHG365', 'weightedPCHG', 'weightedPCHG7', 'weightedPCHG30', 'weightedPCHG180', 'weightedPCHG365', 'avgRETURNS1095', 'avgREVTTM', 'avgMKTCAP', 'totalMKTCAP'];
  const averages = {};
  fields.forEach(field => {
    averages[field] = meanBy(data, field) / 100;
  });
  return { name: 'All Sectors', ...averages };
};

const calculateCombinedSectorValues = (data, groupBy) => {
  const groups = {};

  data?.forEach(item => {
    const group = item[groupBy];
    if (!groups[group]) {
      groups[group] = {
        count: 0, PCHG: [], PCHG7: [], PCHG30: [], PCHG180: [], PCHG365: [],
        weightedPCHG: 0, weightedPCHG7: 0, weightedPCHG30: 0, weightedPCHG180: 0, weightedPCHG365: 0,
        totalMKTCAP: 0, RETURNS1095: 0, REVTTM: 0, MKTCAP: 0
      };
    }
    groups[group].count++;
    if (item.PCHG != null && !isNaN(item.PCHG)) {
      groups[group].PCHG.push(item.PCHG);
      groups[group].weightedPCHG += item.MKTCAP * item.PCHG;
    }
    if (item.PCHG__7 != null && !isNaN(item.PCHG__7)) {
      groups[group].PCHG7.push(item.PCHG__7);
      groups[group].weightedPCHG7 += item.MKTCAP * item.PCHG__7;
    }
    if (item.PCHG__30 != null && !isNaN(item.PCHG__30)) {
      groups[group].PCHG30.push(item.PCHG__30);
      groups[group].weightedPCHG30 += item.MKTCAP * item.PCHG__30;
    }
    if (item.PCHG__180 != null && !isNaN(item.PCHG__180)) {
      groups[group].PCHG180.push(item.PCHG__180);
      groups[group].weightedPCHG180 += item.MKTCAP * item.PCHG__180;
    }
    if (item.PCHG__365 != null && !isNaN(item.PCHG__365)) {
      groups[group].PCHG365.push(item.PCHG__365);
      groups[group].weightedPCHG365 += item.MKTCAP * item.PCHG__365;
    }
    if (item.RETURNS__1095 != null && !isNaN(item.RETURNS__1095)) groups[group].RETURNS1095 += item.RETURNS__1095;
    if (item.REV_TTM != null && !isNaN(item.REV_TTM)) groups[group].REVTTM += item.REV_TTM;
    if (item.MKTCAP != null && !isNaN(item.MKTCAP)) {
      groups[group].MKTCAP += item.MKTCAP;
      groups[group].totalMKTCAP += item.MKTCAP;
    }
  });

  const calculateMedian = (values) => {
    if (values.length === 0) return null;
    values.sort((a, b) => a - b);
    const mid = Math.floor(values.length / 2);
    return values.length % 2 !== 0 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
  };

  const total = Object.values(groups).reduce((acc, group) => acc + group.count, 0);

  return Object.keys(groups).map(key => {
    const group = groups[key];
    return {
      name: key,
      weight: (group.count / total) * 100,
      medianPCHG: calculateMedian(group.PCHG),
      medianPCHG7: calculateMedian(group.PCHG7),
      medianPCHG30: calculateMedian(group.PCHG30),
      medianPCHG180: calculateMedian(group.PCHG180),
      medianPCHG365: calculateMedian(group.PCHG365),
      weightedPCHG: group.totalMKTCAP !== 0 ? (group.weightedPCHG / group.totalMKTCAP) : 0,
      weightedPCHG7: group.totalMKTCAP !== 0 ? (group.weightedPCHG7 / group.totalMKTCAP) * 100 : 0,
      weightedPCHG30: group.totalMKTCAP !== 0 ? (group.weightedPCHG30 / group.totalMKTCAP) * 100 : 0,
      weightedPCHG180: group.totalMKTCAP !== 0 ? (group.weightedPCHG180 / group.totalMKTCAP) * 100 : 0,
      weightedPCHG365: group.totalMKTCAP !== 0 ? (group.weightedPCHG365 / group.totalMKTCAP) * 100 : 0,
      avgRETURNS1095: group.RETURNS1095,
      avgREVTTM: group.REVTTM,
      avgMKTCAP: group.MKTCAP,
      totalMKTCAP: group.totalMKTCAP
    };
  }).sort((a, b) => b.totalMKTCAP - a.totalMKTCAP);
};

const calculateSectorData = (data, sector) => {
  const filteredData = data.filter(item => item.sector === sector);
  const uniqueIndustries = new Set(filteredData.map(item => item.industry));
  const combinedMarketCap = sumBy(filteredData, 'MKTCAP');
  return { uniqueIndustries: uniqueIndustries.size, combinedMarketCap };
};

const calculateIndustryTickers = (data, industry) => {
  const uniqueTickers = new Set(data.filter(item => item.industry === industry).map(item => item.ticker));
  return { uniqueTickers: uniqueTickers.size };
};

const updateWeights = (sectors) => {
  const totalMarketCap = sumBy(sectors, 'totalMKTCAP');
  return sectors.reduce((acc, sector) => {
    if (sector.totalMKTCAP) {
      sector.weight = (sector.totalMKTCAP / totalMarketCap) * 100;
      acc[sector.name] = sector.weight.toFixed(1);
    }
    return acc;
  }, {});
};

const SvgContainer = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '8px',
  '& g:hover': { opacity: 0.9 },
  '& .skeleton:after': {
    animation: '2s linear 0.5s infinite normal none running animation-wiooy9',
    background: 'linear-gradient(90deg, transparent, rgb(0 0 0 / 9%), transparent)',
    content: "''",
    position: 'absolute',
    transform: 'translateX(-100%)',
    inset: '0px',
  }
});

const HeatTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 320,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid rgba(0,0,0,0.08)',
    padding: 0,
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  },
}));

const HeatMap = withTooltip(({ data, groupBy, onClick, colorMetric, heatmapColors, isLoading, showTooltip, hideTooltip, tooltipData, tooltipLeft, tooltipTop }) => {
  const svgRef = useRef(null);
  const [tooltipContent, setTooltipContent] = useState(null);
  const { colors } = heatmapColors && heatmapColors !== "" ? JSON.parse(heatmapColors) : {};

  const getBGColor = (value, type) => {
    const adjustedType = type.replace('weighted', '');
    const result = colors?.find((d) => d.type === adjustedType && value >= d.min && value <= d.max);
    if (isLoading) return '#68686840';
    if (result) return result.color;
    if (value > 0) return 'rgb(49, 85, 49)';
    if (value < 0) return 'rgb(115, 32, 32)';
    return 'rgb(57, 57, 57)';
  };

  useEffect(() => {
    const weights = calculateCombinedSectorValues(data, groupBy);
    const width = svgRef.current?.parentElement?.offsetWidth || 0;
    const height = svgRef.current?.parentElement?.offsetHeight || 0;
    const root = hierarchy({ children: weights }).sum(d => d.totalMKTCAP);
    setTooltipContent(null);
  }, [data, groupBy, onClick, colorMetric]);

  return (
    <SvgContainer className="heat-map">
      {isLoading && <span className="skeleton" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.11)', position: 'relative', height: '100%', overflow: 'hidden' }} />}
      <HeatTooltip title={tooltipContent ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '.9rem' }}>
              {tooltipContent?.name}
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableBody sx={{ '& td, th': { border: 0, color: '#444', whiteSpace: 'nowrap', px: 1.5, py: 0.75 } }}>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontSize: '.8rem' }}>
                    {periodLabels[colorMetric] || '1 Day'} Change
                  </TableCell>
                  <TableCell align="right">
                    <PctPill value={tooltipContent[colorMetric]?.replace('%', '')} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontSize: '.8rem' }}>Market Cap</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '.8rem' }}>{tooltipContent.avgMKTCAP}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontSize: '.8rem' }}>Revenue TTM</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '.8rem' }}>{tooltipContent.avgREVTTM}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null} followCursor>
        <svg
          ref={svgRef}
          role="img"
          aria-label="Sector performance heatmap"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        >
          <Treemap
            root={hierarchy({ children: calculateCombinedSectorValues(data, groupBy) }).sum(d => d.totalMKTCAP)}
            size={[svgRef.current ? svgRef.current.parentElement.offsetWidth : 0, svgRef.current ? svgRef.current.parentElement.offsetHeight : 0]}
            round
          >
            {tree => (
              <Group top={0} left={0}>
                {tree.descendants().map((node, i) => {
                  const width = node.x1 - node.x0;
                  const height = node.y1 - node.y0;
                  const percentage = node.data[colorMetric] ? `${(node.data[colorMetric]).toFixed(2)}%` : '';
                  const displayText = width > 50 && height > 30;
                  const displayPercentage = width > 30 && height > 20;

                  return (
                    <Group
                      key={`node-${i}`}
                      top={node.y0}
                      left={node.x0}
                      onClick={() => {
                        if (node.data && onClick) {
                          onClick(node.data.name);
                        } else {
                          window.open(`${getURL()}/sectors/industry/${node.data.name?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}`, '_blank', 'noreferrer', 'nofollow');
                        }
                      }}
                      onMouseMove={() => {
                        setTooltipContent({
                          name: node.data.name,
                          weight: node.data.weight?.toFixed(2),
                          avgMKTCAP: `$${formatCount(Math.sign(node.data.totalMKTCAP) * node.data.totalMKTCAP, true, 1, Math.sign(node.data.totalMKTCAP))}`,
                          avgREVTTM: `$${formatCount(Math.sign(node.data.avgREVTTM) * node.data.avgREVTTM, true, 1, Math.sign(node.data.avgREVTTM))}`,
                          color: node.data[colorMetric] > 0 ? 'green' : 'red',
                          [colorMetric]: `${(node.data[colorMetric])?.toFixed(2)}%`,
                        });
                      }}
                      onMouseLeave={() => hideTooltip()}
                    >
                      <rect
                        width={width}
                        height={height}
                        fill={getBGColor(node.data[colorMetric], colorMetric)}
                        stroke="rgba(255,255,255,0.5)"
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                        shapeRendering="crispEdges"
                        opacity={0.9}
                        cursor="pointer"
                      />
                      {displayText && (
                        <text x={width / 2} y={height / 2 - 9} dy="0.35em" textAnchor="middle" fontSize={16} fontWeight="600" fill="#fff" pointerEvents="none">
                          {width < 90 ? `${node.data?.name?.slice(0, 4)}...` :
                            width < 240 ? `${node.data?.name?.slice(0, 9)}...` :
                              node.data?.name || ''}
                        </text>
                      )}
                      {displayPercentage && (
                        <text x={width / 2} y={displayText ? (height / 2 + 9) : (height / 2 - 3)} dy="0.35em" textAnchor="middle" fontSize={13} fontWeight="600" fill="#fff" pointerEvents="none">
                          {percentage}
                        </text>
                      )}
                    </Group>
                  );
                })}
              </Group>
            )}
          </Treemap>
        </svg>
      </HeatTooltip>
    </SvgContainer>
  );
});

const SectorHeatMap = ({ sectorList, themeMode, groupedSectors, heatmapColorSettings, isLoading }) => {
  const router = useRouter();
  const [selectedSector, setSelectedSector] = useState(null);
  const [colorMetric, setColorMetric] = useState('weightedPCHG');
  const { uniqueIndustries: industries, combinedMarketCap: marketCap } = groupedSectors && groupedSectors.length > 0 ? calculateSectorData(groupedSectors, selectedSector) : {};

  const handleSectorClick = (sector) => setSelectedSector(sector);

  const sectorData = selectedSector ? groupedSectors.filter(item => item.sector === selectedSector) : groupedSectors;
  const combinedMedianData = calculateCombinedSectorValues(groupedSectors, 'sector');
  const overallAverage = calculateAverages(combinedMedianData);
  const tableData = [...combinedMedianData, overallAverage];
  const sectorWeights = updateWeights(tableData);

  return (
    <>
      {groupedSectors && groupedSectors.length > 0 && (
        <Card>
          <Grid container spacing={0}>
            {/* Desktop sector table */}
            <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ display: { md: 'block', sm: 'none', xs: 'none' } }}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '.9rem', color: 'text.primary' }}>
                    Sectors
                  </Typography>
                  <Select
                    value={colorMetric}
                    onChange={(e) => setColorMetric(e.target.value)}
                    size="small"
                    disabled={isLoading}
                    sx={{
                      fontSize: '.78rem',
                      borderRadius: '8px',
                      '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' },
                    }}
                  >
                    {Object.entries(periodLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value} sx={{ fontSize: '.8rem' }}>{label}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <TableContainer>
                  <Table size="small" aria-label="sector performance table">
                    <TableBody>
                      {/* All Sectors row */}
                      <TableRow
                        hover
                        selected={!selectedSector}
                        onClick={() => setSelectedSector(null)}
                        sx={{
                          cursor: 'pointer',
                          borderLeft: !selectedSector ? '3px solid' : '3px solid transparent',
                          borderLeftColor: !selectedSector ? 'primary.main' : 'transparent',
                          '& td': { borderBottom: '1px solid rgba(0,0,0,0.06)' },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, fontSize: '.82rem', pl: 1.5 }}>All Sectors</TableCell>
                        <TableCell align="right" sx={{ pr: 1 }}>
                          <PctPill value={tableData?.find(d => d.name === 'All Sectors')?.[colorMetric] * 100} />
                        </TableCell>
                      </TableRow>
                      {SECTOR_OPTIONS.map((sector) => {
                        const metric = tableData?.find(d => d.name === sector.shortName);
                        const isActive = sector.shortName === selectedSector;
                        return (
                          <TableRow
                            hover
                            key={sector.shortName}
                            selected={isActive}
                            onClick={() => handleSectorClick(sector.shortName)}
                            sx={{
                              cursor: 'pointer',
                              borderLeft: isActive ? '3px solid' : '3px solid transparent',
                              borderLeftColor: isActive ? 'primary.main' : 'transparent',
                              '&:hover': { backgroundColor: 'rgba(22,27,87,0.03)' },
                              '&.Mui-selected': { backgroundColor: 'rgba(22,27,87,0.04)' },
                              '& td': { borderBottom: '1px solid rgba(0,0,0,0.04)' },
                            }}
                          >
                            <TableCell sx={{ fontWeight: isActive ? 700 : 500, fontSize: '.82rem', pl: 1.5 }}>{sector.legend}</TableCell>
                            <TableCell align="right" sx={{ pr: 1 }}>
                              <PctPill value={metric?.[colorMetric]?.toFixed(2)} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>

            {/* Heatmap column */}
            <Grid size={{ xs: 12, sm: 12, md: 8 }}>
              <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    {/* Desktop title */}
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '.95rem', color: 'text.primary', display: { md: 'block', sm: 'none', xs: 'none' } }}>
                      {selectedSector || 'All Sectors'}
                    </Typography>
                    {/* Mobile sector selector */}
                    <Select
                      value={selectedSector || 'All Sectors'}
                      onChange={(e) => setSelectedSector(e.target.value === 'All Sectors' ? null : e.target.value)}
                      size="small"
                      sx={{
                        display: { md: 'none', sm: 'inline-flex', xs: 'inline-flex' },
                        fontSize: '.95rem',
                        fontWeight: 700,
                        borderRadius: '10px',
                        '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' },
                      }}
                    >
                      <MenuItem value="All Sectors" sx={{ fontSize: '.85rem' }}>All Sectors</MenuItem>
                      {SECTOR_OPTIONS.map(d => (
                        <MenuItem key={d.shortName} value={d.shortName} sx={{ fontSize: '.85rem' }}>{d.legend}</MenuItem>
                      ))}
                    </Select>
                    {selectedSector ? (
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '.85rem', color: 'rgb(100,116,139)', display: 'block', lineHeight: 1.5 }}>
                        This sector includes <b>{industries} industries</b>, representing <b>{sectorWeights[selectedSector]}%</b> of the aggregate market cap.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '.85rem', color: 'rgb(100,116,139)', display: 'block', lineHeight: 1.5 }}>
                        The heat map displays the {periodLabels[colorMetric]?.toLowerCase()} percentage change for {tableData.length - 1} sectors.
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Heatmap */}
                <Box sx={{ height: { xs: '20rem', sm: '24rem', md: '28rem' } }}>
                  {selectedSector ? (
                    <HeatMap data={sectorData} groupBy="industry" colorMetric={colorMetric} heatmapColors={heatmapColorSettings} isLoading={isLoading} />
                  ) : (
                    <HeatMap data={groupedSectors} groupBy="sector" onClick={handleSectorClick} colorMetric={colorMetric} heatmapColors={heatmapColorSettings} isLoading={isLoading} />
                  )}
                </Box>

                {/* Caption */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: '14px', color: 'rgb(148,163,184)' }} />
                  <Typography variant="caption" sx={{ fontSize: '.75rem', color: 'rgb(148,163,184)' }}>
                    {selectedSector
                      ? 'Click on an industry to view detailed information.'
                      : 'Sector size is based on market capitalization.'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      )}

      {sectorData && sectorData.length > 0 && (
        <HeatMapTable
          sectorMetrics={sectorData.sort((a, b) => b.MKTCAP - a.MKTCAP).slice(0, 25)}
          selectedSector={selectedSector}
        />
      )}
    </>
  );
};

export default SectorHeatMap;
